import { FC, useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { useTranslation } from '@hooks/useTranslation';
import { Organization } from '@models/organization.model';
import {
  UserLocale,
  UserOrganizationRoleStatus,
  UserRoles,
  UserStatus,
  getAllowedRolesRequest,
} from '@models/user.model';
import { UserOrganizationRoleRow } from '@components/molecules/UserOrganizationRoleRow';
import { getTranslatedRoleLabel } from '@constants/roleHierarchy';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { toast } from 'react-toastify';

interface CreateEditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  createUser?: (
    organizationId: string,
    status: UserStatus,
    email: string,
    firstName: string,
    lastName: string,
    role: UserRoles,
  ) => void;
  editUser?: (
    organizationId: string,
    status: UserStatus,
    email: string,
    firstName: string,
    lastName: string,
    // Undefined leaves every membership untouched: in memberships mode the roles
    // are managed by the list below, and submitting must not silently re-assign.
    role: UserRoles | undefined,
    userId: string,
  ) => void;
  organizations: Organization[];
  saving: boolean;
  editing?: boolean;
  /**
   * Every organization the user belongs to. Passing this switches the modal into
   * memberships mode: roles are edited per organization instead of through a
   * single role field. The server only sends these to SUPER_ADMIN callers.
   */
  memberships?: {
    organizationId: string;
    role: UserRoles;
    status?: UserOrganizationRoleStatus;
    organization?: { organizationId: string; name: string };
  }[];
  onChangeMembershipRole?: (organizationId: string, role: string) => void;
  /** Organization whose role is mid-save, for the row spinner. */
  savingOrganizationId?: string | null;
  initialData?: {
    email: string;
    firstName: string;
    lastName: string;
    userId: string;
    role: UserRoles;
    locale: UserLocale;
    organizationId?: string;
  } | null;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  role: Yup.string().required('Role is required'),
  // Required in edit mode too: the role assignment always targets a concrete
  // organization, and this is also how an orphaned (organization-less) user
  // gets attached to one. In memberships mode it is optional, because it then
  // means "also add them to this organization" and leaving it blank is valid.
  organizationId: Yup.string().when('manageMemberships', {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Organization is required'),
  }),
  status: Yup.string().when('isCreateMode', {
    is: true,
    then: (schema) => schema.required('Status is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  locale: Yup.string().required('Locale is required'),
});

const CreateEditUserModal: FC<CreateEditUserModalProps> = ({
  isOpen,
  onClose,
  createUser,
  editUser,
  organizations,
  saving,
  editing = false,
  initialData,
  memberships,
  onChangeMembershipRole,
  savingOrganizationId,
}) => {
  const manageMemberships = editing && !!memberships;
  const localeCommon = useTranslation('common');
  const localeOrganizations = useTranslation('organizations');
  const localeUsers = useTranslation('users');
  const localeEnumLabels = useTranslation('enumLabels');
  const { role } = useAppSelector((state: RootState) => state.user);
  const [roles, setRoles] = useState<UserRoles[]>([]);

  const formik = useFormik({
    initialValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
      role: initialData?.role || UserRoles.EMPLOYEE,
      // In memberships mode this pair means "also add them to this organization",
      // so it starts blank rather than pre-filled with an org they already belong
      // to — which would read as a pending change that is really a no-op.
      organizationId: manageMemberships
        ? ''
        : initialData?.organizationId || '',
      status: UserStatus.ACTIVE,
      locale: initialData?.locale || UserLocale.EN,
      isCreateMode: !editing,
      manageMemberships,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (editing && initialData) {
          // No organization picked in memberships mode means this submit only
          // touches the profile. Sending a role without one would fall back to
          // the CALLER's organization on the server and quietly add the user to
          // the super admin's own tenant.
          const assignedRole =
            manageMemberships && !values.organizationId
              ? undefined
              : (values.role as UserRoles);

          editUser &&
            editUser(
              values.organizationId,
              UserStatus.ACTIVE,
              values.email,
              values.firstName,
              values.lastName,
              assignedRole,
              initialData.userId,
            );
        } else {
          createUser &&
            createUser(
              values.organizationId,
              values.status as UserStatus,
              values.email,
              values.firstName,
              values.lastName,
              values.role as UserRoles,
            );
        }
        onClose();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    setFieldTouched,
    submitForm,
    setFieldValue,
    errors,
    touched,
    resetForm,
  } = formik;

  const getAllowedRoles = useCallback(async () => {
    try {
      const data: getAllowedRolesRequest = await graphQlClient.request(
        UserGraphQL.queries.getAllowedRoles,
      );
      setRoles(data?.getAllowedRoles || []);
      if (
        editing &&
        initialData?.role &&
        !data?.getAllowedRoles.includes(initialData?.role) &&
        !(role === UserRoles.SUPER_ADMIN)
      ) {
        toast.error(localeUsers['notAllowedToEdit']);
        onClose();
      }
    } catch (e: any) {
      toast.error(
        e?.response?.errors[0]?.message || localeCommon['requestError'],
      );
    }
  }, [editing, initialData?.role, role, localeUsers, localeCommon, onClose]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  useEffect(() => {
    getAllowedRoles();
  }, [getAllowedRoles]);

  // SUPER_ADMIN is a platform role, not an organization membership, so it is
  // never assignable here even though getAllowedRoles returns it to a super
  // admin (that query answers "which roles do you outrank", not "which roles can
  // you grant on this screen").
  const roleOptions: SelectOption[] = roles
    .filter((roleItem) => roleItem !== UserRoles.SUPER_ADMIN)
    .map((roleItem) => ({
      label: getTranslatedRoleLabel(
        roleItem,
        localeEnumLabels,
        localeCommon.unknown,
      ),
      value: roleItem,
    }));

  // Same set, narrowed to the string type UserOrganizationRoleRow expects.
  const membershipRoleOptions = roleOptions.map((option) => ({
    value: String(option.value),
    label: String(option.label),
  }));

  // In memberships mode this picker only ADDS organizations, so the ones the
  // user is already in are dropped: picking one would silently overwrite the
  // role shown in the list above rather than adding anything.
  const joinedOrganizationIds = new Set(
    (memberships ?? []).map((membership) => membership.organizationId),
  );
  const organizationOptions: SelectOption[] = organizations
    .filter(
      (org) =>
        !manageMemberships || !joinedOrganizationIds.has(org.organizationId),
    )
    .map((org) => ({
      label: org.name,
      value: org.organizationId,
    }));

  // Shared by both layouts below: standalone fields when creating or editing as
  // a non-super-admin, grouped under "Add to another organization" otherwise.
  const roleSelect = (
    <ReactSelect
      name="role"
      onBlur={() => setFieldTouched('role', true, false)}
      value={roleOptions.find((opt) => opt.value === values.role) || null}
      onChange={(selectedOption) => {
        const option = selectedOption as SelectOption | null;
        setFieldValue('role', option?.value || UserRoles.EMPLOYEE);
      }}
      options={roleOptions}
      placeholder={localeCommon['user'] + ' ' + localeUsers['role']}
      isSearchable
    />
  );

  const organizationSelect = (
    <ReactSelect
      name="organizationId"
      onBlur={() => setFieldTouched('organizationId', true, false)}
      value={
        organizationOptions.find(
          (opt) => opt.value === values.organizationId,
        ) || null
      }
      onChange={(selectedOption) => {
        const option = selectedOption as SelectOption | null;
        setFieldValue('organizationId', option?.value || '');
      }}
      options={organizationOptions}
      placeholder={
        manageMemberships
          ? 'Select organization'
          : localeCommon['user'] + ' ' + localeOrganizations['organization']
      }
      isClearable={manageMemberships}
      isSearchable
    />
  );

  const statusOptions: SelectOption[] = [
    { label: 'Enabled', value: UserStatus.ACTIVE },
    { label: 'Disabled', value: UserStatus.DISABLED },
  ];

  const localeOptions: SelectOption[] = [
    { label: 'English', value: UserLocale.EN },
    { label: 'Español', value: UserLocale.ES },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={saving}
      handleConfirm={submitForm}
      title={editing ? localeUsers['editUser'] : localeUsers['addUser']}
      confirmText={editing ? 'Save' : localeCommon['create']}>
      <form className="flex flex-col gap-6">
        <div className="flex gap-4">
          <FormGroup
            error={touched.firstName ? errors.firstName : undefined}
            htmlFor="firstName"
            label={localeCommon['user'] + ' ' + localeUsers['firstName']}>
            <Input
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={
                localeCommon['user'] + ' ' + localeUsers['firstName']
              }
              error={!!(touched.firstName && errors.firstName)}
            />
          </FormGroup>
          <FormGroup
            error={touched.lastName ? errors.lastName : undefined}
            htmlFor="lastName"
            label={localeCommon['user'] + ' ' + localeUsers['lastName']}>
            <Input
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={localeCommon['user'] + ' ' + localeUsers['lastName']}
              error={!!(touched.lastName && errors.lastName)}
            />
          </FormGroup>
        </div>

        <FormGroup
          error={touched.email ? errors.email : undefined}
          htmlFor="email"
          label={localeCommon['user'] + ' ' + localeCommon['email']}>
          <Input
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={localeCommon['user'] + ' ' + localeCommon['email']}
            error={!!(touched.email && errors.email)}
            disabled={editing}
          />
        </FormGroup>

        {manageMemberships && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Organizations & Roles
            </p>
            {memberships!.length > 0 ? (
              <>
                {[...memberships!]
                  .sort((a, b) =>
                    (a.organization?.name ?? '').localeCompare(
                      b.organization?.name ?? '',
                    ),
                  )
                  .map((membership) => (
                    <UserOrganizationRoleRow
                      key={membership.organizationId}
                      organizationName={
                        membership.organization?.name ||
                        membership.organizationId
                      }
                      role={membership.role}
                      status={membership.status}
                      roleOptions={membershipRoleOptions}
                      isSaving={
                        savingOrganizationId === membership.organizationId
                      }
                      onRoleChange={(nextRole) =>
                        onChangeMembershipRole?.(
                          membership.organizationId,
                          nextRole,
                        )
                      }
                    />
                  ))}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Role changes here save immediately and apply to that
                  organization only.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This user does not belong to any organization yet.
              </p>
            )}
          </div>
        )}

        {/* In memberships mode these two only ever describe a NEW membership, so
            they are grouped and labelled as such. Left loose they read as "the"
            role and organization, contradicting the list above. */}
        {manageMemberships ? (
          <div className="flex flex-col gap-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Add to another organization
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Optional. Pick an organization and the role to grant there, then
                save. Organizations already listed above are not shown.
              </p>
            </div>
            <div className="flex gap-4">
              <FormGroup
                error={
                  touched.organizationId ? errors.organizationId : undefined
                }
                htmlFor="organizationId"
                label="Organization">
                {organizationSelect}
              </FormGroup>
              <FormGroup
                error={touched.role ? errors.role : undefined}
                htmlFor="role"
                label="Role">
                {roleSelect}
              </FormGroup>
            </div>
          </div>
        ) : (
          <>
            <FormGroup
              error={touched.role ? errors.role : undefined}
              htmlFor="role"
              label={localeCommon['user'] + ' ' + localeUsers['role']}>
              {roleSelect}
            </FormGroup>

            {role === UserRoles.SUPER_ADMIN && (
              <FormGroup
                error={
                  touched.organizationId ? errors.organizationId : undefined
                }
                htmlFor="organizationId"
                label={
                  localeCommon['user'] +
                  ' ' +
                  localeOrganizations['organization']
                }>
                {organizationSelect}
              </FormGroup>
            )}
          </>
        )}

        {!editing && (
          <FormGroup
            error={touched.status ? errors.status : undefined}
            htmlFor="status"
            label={localeCommon['user'] + ' ' + localeUsers['status']}>
            <ReactSelect
              name="status"
              onBlur={() => setFieldTouched('status', true, false)}
              value={
                statusOptions.find((opt) => opt.value === values.status) || null
              }
              onChange={(selectedOption) => {
                const option = selectedOption as SelectOption | null;
                setFieldValue('status', option?.value || UserStatus.ACTIVE);
              }}
              options={statusOptions}
              placeholder={localeCommon['user'] + ' ' + localeCommon['status']}
              isSearchable={false}
            />
          </FormGroup>
        )}

        <FormGroup
          error={touched.locale ? errors.locale : undefined}
          htmlFor="locale"
          label={localeUsers['locale']}>
          <ReactSelect
            name="locale"
            onBlur={() => setFieldTouched('locale', true, false)}
            value={
              localeOptions.find((opt) => opt.value === values.locale) || null
            }
            onChange={(selectedOption) => {
              const option = selectedOption as SelectOption | null;
              setFieldValue('locale', option?.value || UserLocale.EN);
            }}
            options={localeOptions}
            placeholder={localeUsers['locale']}
            isSearchable={false}
          />
        </FormGroup>
      </form>
    </Modal>
  );
};

export default CreateEditUserModal;
